/**
 * How a page acquires an OEM web component: a runtime `import()` of the one published loader
 * URL, whose module registers the custom element as a side effect of resolving.
 *
 * Resolves to the module itself, because a host that owns the URL needs the view grammar the
 * bundle exports beside the element (`OemViewGrammar`) to translate its location into a
 * `view` and a navigate event back into a path. Those exports are the only part of the module
 * shape a host may rely on; everything else stays the element's own.
 */

/**
 * The bundle is ~6MB, which is roughly 48s on a 1 Mbps connection; rounded up for margin. It
 * grew from 3.4MB to 6MB over three releases (the PDF worker is now inlined into it), so this
 * is sized off the measured artifact rather than a round number.
 * Its only job is to turn an indefinite stall into a reported failure — an `import()` cannot be
 * aborted, so a bound tight enough to catch a merely slow load would report the screens as
 * missing while they are still on the way, and then register them anyway.
 */
export const OEM_ELEMENT_LOAD_TIMEOUT_MS = 90_000;

/**
 * One promise per URL per document. `customElements.define` is global and cannot be undone, so
 * the module must be evaluated exactly once and must never be "unloaded" on unmount — a second
 * import is also a second 6MB on a cold cache.
 */
const loads = new Map<string, Promise<unknown>>();

export class OemElementLoadTimeoutError extends Error {
  constructor(url: string) {
    super(
      `The OEM element at ${url} did not arrive within ${OEM_ELEMENT_LOAD_TIMEOUT_MS}ms`
    );
    this.name = "OemElementLoadTimeoutError";
  }
}

export class OemElementNotPublishedError extends Error {
  constructor() {
    super("No OEM element is published for this environment");
    this.name = "OemElementNotPublishedError";
  }
}

export function loadOemElement(
  url: string,
  tagName: string,
  /**
   * Injected only by tests: a real `import()` either resolves or rejects, and there is no way
   * to make one hang, so the timeout is the one branch no real load can reach.
   */
  importModule: (target: string) => Promise<unknown> = target =>
    // `webpackIgnore` is required, not decorative: this app is built by webpack, which would
    // otherwise try to resolve a URL that only exists on another origin at build time.
    import(/* webpackIgnore: true */ target)
): Promise<unknown> {
  // Rejected rather than left pending: an environment with no published artifact is a real
  // state, and the caller's failure surface is the right thing to show for it.
  if (url === "") return Promise.reject(new OemElementNotPublishedError());

  const started = loads.get(url);
  if (started !== undefined) return started;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const load = Promise.race([
    // The registry is the contract, not the import resolving. In practice the bundle calls
    // `define` during evaluation so the two coincide, but waiting on the registry is what the
    // caller actually needs before it may create the tag. Both steps sit inside the race, so
    // one deadline covers the whole acquisition: a module that resolves but never defines the
    // tag is as much an indefinite stall as an import that never settles.
    importModule(url).then(module =>
      customElements.whenDefined(tagName).then(() => module)
    ),
    new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () => reject(new OemElementLoadTimeoutError(url)),
        OEM_ELEMENT_LOAD_TIMEOUT_MS
      );
    })
  ])
    .catch((error: unknown) => {
      // Dropped from the cache so a remount retries. A failed load is usually a deploy in
      // flight or a cold-start network fault, and caching the rejection would leave the page
      // permanently empty until the tab is reloaded.
      loads.delete(url);
      throw error;
    })
    .finally(() => clearTimeout(timer));

  loads.set(url, load);
  return load;
}
